import {
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";

import {
  messageBundle,
  storeNode,
  tsx,
} from "@arcgis/core/widgets/support/widget";

import Widget from "@arcgis/core/widgets/Widget";
import { VNode } from "src/interfaces/interfaces";

import "./AppTile/styles/AppTile.scss";
import AppTypeInfoModal from "./AppTypeInfoModal";
import t9nAppMessages from "../../t9n/App/App.json";
import ContentAreaViewModel from "../ui/ContentArea/ContentAreaViewModel";
import AppTypeTile from "./AppTypeTile";
import PublishForm from "./PublishForm";
import IFrameAppPreview from "./IFrameAppPreview";

import CircleDownCarotSVG from "../../../public/assets/images/Info_icon.svg";
import GenericMyAppsImg from "../../../public/assets/images/GenericMyAppImg.png";
import { keyboardAccessibleHelper } from "../utils/appUtils";
import { when } from "@arcgis/core/core/reactiveUtils";
import { focusElement } from "../utils/focusUtils";
import DeleteModal from "./DeleteModal";
import AppStudioModal from "./AppStudioModal";
import { applyShadowStyle } from "../utils/utils";
import InfoBadge from "./InfoBadge";

import {
  SHADOW_STYLE_CARD,
  SHADOW_STYLE_DROPDOWN,
} from "../utils/shadowStyles";
import { EAppTemplateType } from "templates-common-library/baseClasses/CompatibilityChecker";
import PortalQueryParams from "@arcgis/core/portal/PortalQueryParams";
import * as intl from "@arcgis/core/intl.js";

export interface AppTileProperties extends __esri.WidgetProperties {
  appInfo: __esri.PortalItem;
  portal: __esri.Portal;
  contentAreaVM: ContentAreaViewModel;
}

export interface AppData {
  folderId: string;
  source: string;
  values: {
    draft?: any;
    [fieldName: string]: any;
  };
}

export interface ddButtonDef {
  icon: string;
  onClickFunc: Function;
  label: string;
  primaryMarker?: boolean;
  labelHoverText?: string;
}

export type ResourceItem = __esri.PortalItem | __esri.PortalGroup | string;

@subclass("Landing.widgets.AppTile")
export default class AppTile extends Widget {
  @property()
  identifier: "AppTile" = "AppTile";

  @property()
  appInfo: __esri.PortalItem;

  @property()
  portal: __esri.Portal;

  @property()
  contentAreaVM: ContentAreaViewModel;

  @property()
  resourceItem: ResourceItem = undefined; // (undefined => null) || (undefined => __esri.PortalItem) --- This is how we know loading is done

  @property()
  appTypeTemplate: AppTypeTile;

  @property()
  infoModal: AppTypeInfoModal;

  @property()
  saveCopyModal: PublishForm;

  @property()
  chooseFromExistingModal: PublishForm;

  @property()
  iFramePreviewModal: IFrameAppPreview;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof t9nAppMessages;

  @property()
  dropDownMenuButton: HTMLCalciteButtonElement;

  @property()
  appTileDropDownOptions: ddButtonDef[];

  @property()
  _configureBtn: ddButtonDef;
  @property()
  _chooseWithExistingBtn: ddButtonDef;
  @property()
  _copyBtn: ddButtonDef;
  @property()
  _deleteBtn: ddButtonDef;
  @property()
  _deleteModal: DeleteModal;
  @property()
  _orgUrl: string;
  @property()
  _previewBtn: ddButtonDef;
  @property()
  _viewAppBtn: ddButtonDef;
  @property()
  _viewDetailsBtn: ddButtonDef;
  @property()
  _appData: AppData;
  @property()
  _appStudioModal: AppStudioModal;

  /** Saved active element when a modal is launched */
  @property()
  _activeElement: HTMLElement;

  @property()
  appTemplateTitleLocalized: string;

  @property()
  thumbnailUrl: string;

  private _isDeprecated: boolean = false;
  private _deprecatedBadge: InfoBadge;

  constructor(properties?: AppTileProperties) {
    super(properties);
  }

  postInitialize() {
    this._populateDDOptions();

    when(
      () => this?.contentAreaVM?.publishAppVM?.orgUrl !== undefined,
      () => {
        this._orgUrl = this?.contentAreaVM?.publishAppVM?.orgUrl;
        this._initDeleteModal();
        this._initAppStudioModal();
      },
      {
        once: true,
        initial: true,
      }
    );

    when(
      () => this?.contentAreaVM?.appTypeInfoVM?.isAppTypeDataLoaded === true,
      () => {
        // Find App Template Title Localized String
        this.appTemplateTitleLocalized = [
          ...(this?.contentAreaVM?.appTypeInfoVM?.appTypeInfo != null
            ? this.contentAreaVM.appTypeInfoVM.appTypeInfo
            : []),
          {
            // Have to hardcode deprecated minimalist app so it still gets in the templates filter list
            title: this?.appMessages?.deprecatedApps?.minimalist,
            url: "/apps/instant/minimalist/index.html",
          },
        ]?.find((appTypeInfo) => {
          return this.appInfo.url.indexOf(appTypeInfo.url) !== -1;
        })?.title;
      },
      {
        once: true,
        initial: true,
      }
    );

    // Pull App Info's App Data
    this.contentAreaVM.myAppsDataVM
      .pullAppDataMemoized(this.appInfo)
      .then(async (data: AppData) => {
        this._appData = data;

        this._findResourceData(data);

        const appTypeItemId: string = data.source;

        when(
          () => this?.contentAreaVM?.browseAllTiles !== undefined,
          async () => {
            this.appTypeTemplate = this?.contentAreaVM?.browseAllTiles.find(
              (appTemplate: AppTypeTile) => {
                const actualAppTemplateId = appTemplate?.appTypeInfo?.appTypeId;
                return actualAppTemplateId === appTypeItemId;
              }
            );

            // If the AppTypeTemplate doesn't already exist in the Landing Page, then we need to query for the info.
            // This happens with apps having been created in different locales. (each Instant App has different IDs depending on the locale)
            if (this.appTypeTemplate == undefined) {
              const { templateItemMemoizationTable } =
                this.contentAreaVM.myAppsDataVM;
              const memoizedTemplate =
                templateItemMemoizationTable.get(appTypeItemId);
              if (memoizedTemplate) {
                this.appTypeTemplate = memoizedTemplate;
              } else {
                const { results } = await this.portal.queryItems({
                  query: `id:${appTypeItemId}`,
                });
                if (results.length > 0) {
                  this.appTypeTemplate = new AppTypeTile(
                    this.contentAreaVM.formatAppTypeItems(results?.[0], 0)
                  );
                  templateItemMemoizationTable.set(
                    appTypeItemId,
                    this.appTypeTemplate
                  );
                }
              }
            }

            this._isDeprecated =
              this.contentAreaVM.appTypeInfoVM.deprecatedAppList.includes(
                this?.appTypeTemplate?.appTypeInfo?.appTypeUrlFragment
              );

            if (this._isDeprecated) {
              this._deprecatedBadge = new InfoBadge({
                badge: "Deprecated",
                appType: null,
                disableTooltip: true,
                overrideTooltip: this.appMessages.appBadgeToolTips.Deprecated,
                uniqueId: `deprecated-badge-${this.appInfo.id}`,
              });
              // recalc options
              this._populateDDOptions();
            }
          },
          {
            once: true,
            initial: true,
          }
        );
      });

    this._getAppInfoThumbnailUrl().then((url) => {
      this.thumbnailUrl = url;
    });
  }

  render() {
    const { title, modified } = this.appInfo;
    const { modifiedMsg, owner } = this.appMessages.myApps;

    return (
      <div class="app-tile" id={"app-tile-" + this.appInfo.id}>
        <calcite-card
          afterCreate={(card) => applyShadowStyle(SHADOW_STYLE_CARD, card)}
          class="app-card"
        >
          {this._renderImg()}

          <h2 slot="title" class="app_title" title={title}>
            {title}
          </h2>

          {this._renderAppTemplateInfo()}

          <div class="app-metadata__map-title">
            {this._resourceInfoDisplay()}
          </div>

          <div class="app-metadata__app-owner">
            <span>
              <strong>{owner}:</strong>
              &nbsp;
              <calcite-link
                icon-end="launch"
                href={`${this._orgUrl}/home/user.html?user=${this.appInfo.owner}`}
                target="_blank"
              >
                {this.appInfo.owner}
              </calcite-link>
            </span>
          </div>

          <div class="app-metadata__bottom-row">
            <div class="app-metadata__modified-date">
              <calcite-icon icon="map" scale="s"></calcite-icon>
              <span>
                {modifiedMsg}{" "}
                {intl.formatDate(modified, { dateStyle: "short" })}
              </span>
            </div>
          </div>

          <div class="app-tile__bottom-buttons">{this._renderFABs()}</div>
        </calcite-card>

        {this.infoModal?.render()}
        {this.saveCopyModal?.render()}
        {this.chooseFromExistingModal?.render()}
        {this.iFramePreviewModal?.render()}
        {this._deleteModal?.render()}
        {this._appStudioModal?.render()}
      </div>
    );
  }

  private _initDeleteModal() {
    this._deleteModal = new DeleteModal({
      appInfo: this.appInfo,
      orgUrl: this._orgUrl,
      contentAreaVM: this.contentAreaVM,
      portal: this.portal,
    });
  }

  private _initAppStudioModal() {
    this._appStudioModal = new AppStudioModal({
      appInfo: this.appInfo,
      orgUrl: this._orgUrl,
      contentAreaVM: this.contentAreaVM,
    });
  }

  private _renderImg(): VNode {
    const { viewApp } = this.appMessages.myApps;
    return (
      <div
        class="app-tile__img-container"
        slot="thumbnail"
        data-overlay-desc={viewApp}
        onclick={this._openApp.bind(this)}
        onkeydown={keyboardAccessibleHelper.bind(
          this,
          this._openApp.bind(this)
        )}
        tabindex="0"
        title={viewApp}
      >
        <calcite-icon
          class="app-tile__img-launch"
          icon="launch"
          scale="m"
        ></calcite-icon>
        <img class="app-tile__img" alt="" src={this.thumbnailUrl} />
      </div>
    );
  }

  private async _getAppInfoThumbnailUrl(): Promise<string> {
    let { thumbnailUrl } = this.appInfo || {};
    if (thumbnailUrl?.indexOf("ago_downloaded.png") !== -1) {
      // if app was modified after August 24th, 2023, then load the appinfo again
      // this is for new changes to the thumbnails of old apps (https://devtopia.esri.com/WebGIS/arcgis-template-landing/issues/595)
      const date = new Date(2023, 7, 23);
      if (this.appInfo.modified.getTime() > date.getTime()) {
        await this.appInfo.load();
        thumbnailUrl = this.appInfo.thumbnailUrl;
      }

      // test again
      if (thumbnailUrl?.indexOf("ago_downloaded.png") !== -1) {
        return await this._getAppTypeThumbnailUrl();
      }
    }
    return thumbnailUrl != null ? `${thumbnailUrl}&w=800` : GenericMyAppsImg;
  }

  private _getAppTypeThumbnailUrl(): Promise<string> {
    return new Promise((resolve) => {
      // use reactiveutils to wait until appTypeInfo is defined
      when(
        () => this?.appTypeTemplate?.appTypeInfo !== undefined,
        () => {
          const { screenshotUrl } = this?.appTypeTemplate?.appTypeInfo || {};
          resolve(screenshotUrl != null ? screenshotUrl : GenericMyAppsImg);
        },
        {
          once: true,
          initial: true,
        }
      );
    });
  }

  private _renderAppTemplateInfo(): VNode {
    const { appTemplate } = this.appMessages.myApps;
    const { openAppDescription } = this?.appMessages?.appDescriptions;
    return (
      <div class="app-metadata__template">
        <strong>{appTemplate}:&nbsp;</strong>
        {this.appTemplateTitleLocalized != null
          ? this.appTemplateTitleLocalized
          : this?.appTypeTemplate?.appTypeInfo?.appTypeTitle}
        &nbsp;
        <img
          src={CircleDownCarotSVG}
          tabindex="0"
          role="button"
          title={openAppDescription}
          aria-label={openAppDescription}
          onclick={this._initializeInfoModal.bind(this)}
          onkeydown={keyboardAccessibleHelper.bind(
            this,
            this._initializeInfoModal.bind(this)
          )}
          class="apptile-down-chevron-svg"
          alt=""
        />
        &nbsp;
        <span title={this.appMessages.appBadgeToolTips.Deprecated}>
          {this?._deprecatedBadge?.render()}
        </span>
      </div>
    );
  }

  private _populateDDOptions() {
    const { preview, viewApp, ddButtonsOptions } = this.appMessages.myApps;

    const { choose, configure, saveCopy, viewDetails, deleteLabel } =
      ddButtonsOptions;

    this._chooseWithExistingBtn = {
      icon: "applications",
      label: choose,
      onClickFunc: () => {
        this._initializeChooseWithExisting();
      },
    };
    this._configureBtn = {
      icon: "gear",
      label: configure,
      onClickFunc: () => {
        window.open(
          `${this._orgUrl}/apps/configure-template/index.html?appid=${this.appInfo.id}`
        );
      },
      primaryMarker: true,
    };
    this._copyBtn = {
      icon: "copy",
      label: saveCopy,
      onClickFunc: () => {
        this._initializeSaveCopy();
      },
    };
    this._viewDetailsBtn = {
      icon: "infographic",
      label: viewDetails,
      onClickFunc: () => {
        window.open(`${this._orgUrl}/home/item.html?id=${this.appInfo.id}`);
      },
    };
    this._deleteBtn = {
      icon: this.portal?.sourceJSON?.recycleBinEnabled
        ? "recycle-bin"
        : "trash",
      label: deleteLabel,
      onClickFunc: () => {
        this._deleteModal.showModal();
      },
    };
    this._previewBtn = {
      icon: "camera-flash-on",
      label: preview,
      onClickFunc: () => {
        this._initializePreview();
      },
    };
    this._viewAppBtn = {
      icon: "launch",
      label: viewApp,
      onClickFunc: () => {
        this._openApp();
      },
    };

    this._assignAvailableOptions();
  }

  private _assignAvailableOptions() {
    let options: ddButtonDef[] = [];

    // Signed in user owns this app
    if (this.appInfo.owner === (this.portal as any).credential.userId) {
      options.push(this._configureBtn);

      if (this._isNoWebmapWorkflow()) {
        options.push(
          ...(!this._isDeprecated
            ? [this._chooseWithExistingBtn, this._copyBtn]
            : [])
        );
      } else {
        options.push(
          ...(!this._isDeprecated
            ? [this._chooseWithExistingBtn, this._previewBtn, this._copyBtn]
            : [])
        );
      }

      options.push(
        ...[this._viewAppBtn, this._viewDetailsBtn, this._deleteBtn]
      );

      if (this._shouldAddAppStudioIntegration()) {
        options.push({
          icon: "app-run",
          label: this?.appMessages?.myApps?.ddButtonsOptions?.appStudio,
          onClickFunc: () => {
            this._appStudioModal.showModal();
          },
        });
      }
    } else {
      // Signed in user does not own this app
      if (this.appInfo.typeKeywords.includes("useOnly")) {
        // Copy option on this item is disabled by owner - https://devtopia.esri.com/WebGIS/arcgis-template-landing/issues/598
        options.push(...[this._viewAppBtn, this._viewDetailsBtn]);
      } else {
        options.push(
          ...[this._copyBtn, this._viewAppBtn, this._viewDetailsBtn]
        );
      }
    }

    this.appTileDropDownOptions = options;
  }

  private _shouldAddAppStudioIntegration(): boolean {
    const templateType = this.appInfo.url;
    return (
      templateType.includes(EAppTemplateType.AttachmentViewer) ||
      templateType.includes(EAppTemplateType.Nearby) ||
      templateType.includes(EAppTemplateType.ZoneLookup) ||
      templateType.includes(EAppTemplateType.Sidebar)
    );
  }

  _renderFABs(): VNode[] {
    return this?.appTileDropDownOptions?.map(
      (option: ddButtonDef, index: number) => {
        const { icon, label, onClickFunc, primaryMarker } = option;
        const id = `${this.appInfo.id}-${icon}-${index}`;
        const fabWrapperId = `fab-wrapper-${id}`;
        const fabTooltipId = `tooltip-${id}`;
        const fabId = `fab-${id}`;
        return (
          <div key={fabWrapperId}>
            <calcite-fab
              appearance={primaryMarker ? "solid" : "outline-fill"}
              class="appTileDropDownLinks"
              kind={primaryMarker ? "brand" : "brand"}
              icon={icon}
              id={fabId}
              aria-label={label}
              title={label}
              onclick={(e) => {
                e.preventDefault();
                onClickFunc.call(this, e);
              }}
              onkeydown={keyboardAccessibleHelper.bind(
                this,
                onClickFunc.bind(this)
              )}
            ></calcite-fab>
            {/* <calcite-tooltip
              label={label}
              reference-element={fabId}
              placement="auto"
            >
              {label}
            </calcite-tooltip> */}
          </div>
        );
      }
    );
  }

  private async _queryForPortalItem(
    itemId: string
  ): Promise<__esri.PortalItem | null> {
    if (itemId == null) {
      return;
    }
    const q: __esri.PortalQueryParams = {} as any;
    q.query = `id:"${itemId}"`;
    const res: __esri.PortalQueryResult = await this.portal.queryItems(q);
    if (res.results.length > 0) {
      return res.results[0];
    } else {
      return null;
    }
  }

  private _initializeInfoModal() {
    this._saveActiveElem();
    const { appTypeTitle, thumbnailUrl, appTypeUrlFragment } =
      this?.appTypeTemplate?.appTypeInfo;
    this.infoModal = new AppTypeInfoModal({
      portal: this.portal,
      picUrl: thumbnailUrl,
      appTypeTitle:
        this.appTemplateTitleLocalized != null
          ? this.appTemplateTitleLocalized
          : appTypeTitle,
      appTypeUrlFragment,
      sendFocusOnClose: this._focusPriorActiveElem(),
    });
  }

  private _initializeSaveCopy() {
    this._saveActiveElem();
    const { title, successMsg, failureMsg } =
      this.appMessages.myApps.saveCopyAlert;
    const { appTypeInfo, publishAppVM } = this?.appTypeTemplate;
    this.saveCopyModal = new PublishForm({
      ui: "modal",
      mode: "copyApp",
      appTypeInfo,
      existingAppItem: this.appInfo,
      portal: this.portal,

      publishAppVM,
      feedbackFunction: (msg: string, success: boolean, appId?: string) => {
        this.contentAreaVM.showAlert(
          title,
          success ? `${successMsg}` : `${failureMsg}: ${msg}`,
          success ? "success" : "error",
          appId
        );
        this.contentAreaVM.myAppsDataVM.performQuery(true);
      },
      sendFocusOnClose: this._focusPriorActiveElem(),
    });
  }

  private _initializeChooseWithExisting() {
    this._saveActiveElem();
    const { appTypeInfo, publishAppVM, resourceInfo } = this?.appTypeTemplate;
    this.chooseFromExistingModal = new PublishForm({
      ui: "modal",
      mode: "fromExistingApp",
      appTypeInfo,
      existingAppItem: this.appInfo,
      resourceInfo,
      portal: this.portal,

      publishAppVM,
      feedbackFunction: (msg: string, success: boolean, appId?: string) => {
        this.contentAreaVM.myAppsDataVM.performQuery();
      },
      sendFocusOnClose: this._focusPriorActiveElem(),
    });
  }

  private _initializePreview(): void {
    this._saveActiveElem();
    const { appTypeInfo, publishAppVM, resourceInfo } = this?.appTypeTemplate;
    this.iFramePreviewModal = new IFrameAppPreview({
      appTypeInfo,
      resourceInfo,
      publishAppVM,
      previewType: "mapPreview",
      existingApp: this.appInfo,
      sendFocusOnClose: this._focusPriorActiveElem(),
    });
  }

  private _isNoWebmapWorkflow(): boolean {
    return this.contentAreaVM.itemLoaderVM.loadedItemType === "none";
  }

  private _openApp() {
    window.open(this.appInfo.url, "_blank");
  }

  private _saveActiveElem() {
    this._activeElement = document.activeElement as HTMLElement;
  }

  private _focusPriorActiveElem(): Function {
    return focusElement.bind(this, this._activeElement);
  }

  /** true if current appInfo is the template type */
  private _checkTemplateType(template: EAppTemplateType): boolean {
    return this.appInfo.url.indexOf(template) !== -1;
  }

  private _getCurrentData(data: AppData, keyValue: string): any {
    return data?.values?.[keyValue] ?? data?.values?.draft?.[keyValue];
  }

  private _formatForNumericDisplay(word: string, amount: number): string {
    return `${word}---${amount}`;
  }

  private _findResourceData(data: AppData): void {
    const { url } = this.appInfo;
    const { items, maps, mapsAndLayers } = this.appMessages.myApps;

    const { resourceDataMemoizationTable } = this.contentAreaVM.myAppsDataVM;
    if (this._checkTemplateType(EAppTemplateType.Portfolio)) {
      // Porfolio Special Case
      this.resourceItem = this._formatForNumericDisplay(
        items,
        this._getCurrentData(data, "itemCollection")?.length ?? 0
      );
    } else if (this._checkTemplateType(EAppTemplateType.Manager)) {
      // Manager Special Case
      this.resourceItem = this._formatForNumericDisplay(
        maps,
        this._getCurrentData(data, "mapItemCollection")?.length ?? 0
      );
    } else if (
      this._checkTemplateType(EAppTemplateType.Atlas) &&
      this._getCurrentData(data, "contentSourceType") === "content" // Atlas Special Case
    ) {
      this.resourceItem = this._formatForNumericDisplay(
        mapsAndLayers,
        (this._getCurrentData(data, "atlasMapIds")?.length ?? 0) +
          (this._getCurrentData(data, "atlasLayerIds")?.length ?? 0)
      );
    } else if (this._isGroupApp(url)) {
      // Group Based Templates Case
      const groupId = data?.values?.group || data?.values?.draft?.group;

      const lookupRes = resourceDataMemoizationTable.get(groupId);
      if (lookupRes) {
        this.resourceItem = lookupRes;
      } else {
        const queryParams = new PortalQueryParams({
          query: `id:"${groupId}"`,
          disableExtraQuery: true, // stops orgId from being appended to query
          sortField: "modified",
          sortOrder: "desc",
          num: 100,
        } as any);

        this.portal.queryGroups(queryParams).then((groupResult) => {
          const groupRes = groupResult.results;
          this.resourceItem = groupRes?.[0];
          resourceDataMemoizationTable.set(groupId, this.resourceItem);
        });
      }
    } else {
      const resourceId: string =
        data?.values?.webmap ||
        data?.values?.draft?.webmap ||
        data?.values?.webscene ||
        data?.values?.draft?.webscene;

      const lookupRes = resourceDataMemoizationTable.get(resourceId);
      if (lookupRes) {
        this.resourceItem = lookupRes;
      } else {
        // Query for App's Webmap
        this._queryForPortalItem(resourceId).then((item: __esri.PortalItem) => {
          this.resourceItem = item;
          resourceDataMemoizationTable.set(resourceId, this.resourceItem);
        });
      }
    }
  }

  private _resourceInfoDisplay(): VNode {
    const { unknown } = this.appMessages.myApps;

    const { url } = this.appInfo;
    if (url == null) {
      return <strong>{unknown}</strong>;
    } else if (typeof this.resourceItem === "string") {
      const numericInfo = this.resourceItem.split("---");
      const amountType = numericInfo[0];
      const amount = Number(numericInfo[1]);
      return (
        <span>
          <strong>{amountType}:</strong>{" "}
          {amount != null ? intl.formatNumber(amount) : 0}
        </span>
      );
    } else if (this._isGroupApp(url)) {
      return this._renderGroupInfo();
    } else {
      return this._renderWebMapOrSceneInfo();
    }
  }

  private _renderGroupInfo(): VNode {
    const { group, noResource } = this.appMessages.myApps;
    const groupTitle = (this?.resourceItem as __esri.PortalGroup)?.title;
    const id = (this?.resourceItem as __esri.PortalGroup)?.id;
    if (groupTitle != null) {
      // The Title exists, meaning the group exists, so we can link out to it
      return (
        <span>
          <strong>{group}:</strong>
          &nbsp;
          <calcite-link
            icon-end="launch"
            href={`${this._orgUrl}/home/group.html?id=${id}`}
            target="_blank"
          >
            {groupTitle}
          </calcite-link>
        </span>
      );
    } else {
      // group has not been picked yet in this app
      return (
        <span>
          <strong>{group}:</strong>
          &nbsp;
          {noResource}
        </span>
      );
    }
  }

  private _renderWebMapOrSceneInfo(): VNode {
    const { map, scene, noResource } = this.appMessages.myApps;
    const { type, title, id } = (this?.resourceItem as __esri.PortalItem) || {};
    const resourceLabel: string = type === "Web Scene" ? scene : map;
    const urlSection: string =
      type === "Web Scene"
        ? "/home/webscene/viewer.html?webscene="
        : "/apps/mapviewer/index.html?webmap=";
    if (title != null) {
      // The Title exists, meaning the webmap/webscene exists, so we can link out to it
      return (
        <span>
          <strong>{resourceLabel}:</strong>
          &nbsp;
          <calcite-link
            icon-end="launch"
            href={`${this._orgUrl}${urlSection}${id}`}
            target="_blank"
          >
            {title}
          </calcite-link>
        </span>
      );
    } else {
      // webmap/webscene has not been picked yet in this app
      return (
        <span>
          <strong>{resourceLabel}:</strong>
          &nbsp;
          {noResource}
        </span>
      );
    }
  }

  private _isGroupApp(url: string): boolean {
    return (
      url.indexOf(EAppTemplateType.CategoryGallery) !== -1 ||
      url.indexOf(EAppTemplateType.Atlas) !== -1
    );
  }
}
